"use client";
import Image from "next/image";
import { AnimatePresence, motion, MotionValue } from "framer-motion";
import { HouseRule } from "@/lib/types";

interface RulePreviewCardProps {
  rule: HouseRule | null;
  x: MotionValue<number>;
  y: MotionValue<number>;
}

// The proof that follows the cursor across the rules list. Desktop only: it is
// a pointer affordance, and on touch there is no pointer to follow — the rows
// open their detail inline instead.
const RulePreviewCard = ({ rule, x, y }: RulePreviewCardProps) => (
  <motion.div
    aria-hidden
    // Centred on the cursor via the transform itself rather than a Tailwind
    // translate class — both write `transform`, and the class would win.
    style={{ x, y, translateX: "-50%", translateY: "-50%" }}
    className="pointer-events-none absolute left-0 top-0 z-20 hidden lg:block"
  >
    <AnimatePresence mode="wait">
      {rule && (
        <motion.div
          key={rule.number}
          initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: -3 }}
          exit={{ opacity: 0, scale: 0.9, rotate: 4 }}
          transition={{ type: "spring", stiffness: 280, damping: 24 }}
          className="w-72 overflow-hidden rounded-2xl border-2 border-black bg-accent-foreground shadow-[8px_8px_0_0_var(--color-primary)] xl:w-80"
        >
          {/* Sized by the image itself rather than dropped into a fixed box —
              these are case-study covers with composed edges, and cropping them
              to a 4:3 window cut the work in half. */}
          <Image
            src={rule.image}
            alt=""
            sizes="(max-width: 1280px) 288px, 320px"
            className="h-auto w-full"
          />
          <p className="px-4 py-3 text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {rule.proof}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

export default RulePreviewCard;
